import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';

import {
    GET_MATCHED_LAST_BRIEF_API,
    type GetMatchedLastBrief,
} from '~/routes/remix-api.get-matched.last-brief';

export interface LastBriefLookup {
    brief: GetMatchedLastBrief | undefined;
    /** The answer is in, either way — including "there was no last brief". */
    settled: boolean;
    /** A request is in flight, so giving up on it now would be premature. */
    loading: boolean;
}

/**
 * What the reader told us about themselves last time.
 *
 * Asked for once per mount and held in a ref rather than derived from the
 * result: a failed request returns nothing, which is indistinguishable from
 * not having asked, and a guard that cannot tell those apart re-fires forever.
 *
 * The route always answers with a `userType` key — null when there is no prior
 * brief — so arrival of the payload is what "settled" means, not truthiness of
 * what is in it.
 */
export const useLastBrief = (enabled: boolean): LastBriefLookup => {
    const fetcher = useFetcher<GetMatchedLastBrief>();
    const requested = useRef(false);

    useEffect(() => {
        if (!enabled || requested.current) return;
        requested.current = true;
        fetcher.load(GET_MATCHED_LAST_BRIEF_API);
    }, [enabled, fetcher]);

    const loading = fetcher.state !== 'idle';

    return {
        brief: fetcher.data,
        loading,
        settled: !enabled || fetcher.data !== undefined,
    };
};
